import React from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';
import { useConferenceContext } from '../../contexts/ConferenceContext';

/**
 * Secondary navigation for everything that isn't submissions.
 *
 * The conference bar carried eight tabs: Submissions, Team, Programme,
 * Announcements, Settings, Reviewers, Review form, Emails. Seven of those are
 * things a chair touches once when setting the conference up and then rarely
 * again; the eighth is where the actual work happens, every day. Giving them
 * equal weight in one row made the daily screen one of eight equal choices, and
 * made the bar long enough to scroll on a laptop.
 *
 * So the bar is now Submissions and Conference settings, and this row appears
 * underneath once you are inside settings — the standard two-level shape:
 * the top level is what you're doing, the second is which part of it.
 *
 * The list deliberately mixes two kinds of destination. Team, Programme,
 * Announcements and Settings are `?tab=` views of the management dashboard;
 * Review form and Emails are separate routes. That is an accident of how the
 * app grew, and it is invisible here on purpose — from the chair's point of
 * view they are all just parts of setting up a conference.
 */

/** The one source of truth for what "settings" contains. */
export const SETTINGS_SECTIONS = [
  { key: 'settings',      path: '/conference-management-dashboard', tab: 'settings',      icon: 'Settings',     label: 'conferenceBar.settings',      fallback: 'Details' },
  { key: 'team',          path: '/conference-management-dashboard', tab: 'team',          icon: 'Users',        label: 'conferenceBar.team',          fallback: 'Team' },
  { key: 'program',       path: '/conference-management-dashboard', tab: 'program',       icon: 'CalendarDays', label: 'conferenceBar.program',       fallback: 'Programme' },
  { key: 'announcements', path: '/conference-management-dashboard', tab: 'announcements', icon: 'Megaphone',    label: 'conferenceBar.announcements', fallback: 'Announcements' },
  { key: 'badges',        path: '/conference-management-dashboard', tab: 'badges',        icon: 'IdCard',       label: 'conferenceBar.badges',        fallback: 'Badges', isNew: true },
  { key: 'reviewForm',    path: '/review-schema-builder',           tab: null,            icon: 'ListChecks',   label: 'conferenceBar.reviewForm',    fallback: 'Review form' },
  { key: 'emails',        path: '/email-template-management-system', tab: null,           icon: 'Mail',         label: 'conferenceBar.emails',        fallback: 'Emails' },
];

/** Routes and tabs that count as "inside settings" — used by the bar too. */
export const SETTINGS_TABS = ['settings', 'team', 'program', 'announcements', 'badges'];
export const SETTINGS_ROUTES = ['/review-schema-builder', '/email-template-management-system'];

/** Is the current location one of the settings sections? */
export function isSettingsLocation(pathname, tab) {
  if (SETTINGS_ROUTES.includes(pathname)) return true;
  return pathname === '/conference-management-dashboard' && SETTINGS_TABS.includes(tab);
}

export default function ConferenceSettingsNav() {
  const { t } = useTranslation('common');
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { conferenceId } = useConferenceContext();

  const currentTab = searchParams.get('tab') || undefined;
  if (!conferenceId || !isSettingsLocation(location?.pathname, currentTab)) return null;

  const href = (s) =>
    `${s.path}?conferenceId=${conferenceId}${s.tab ? `&tab=${s.tab}` : ''}`;

  return (
    <div className="bg-background border-b border-border">
      <div className="page-shell px-4 sm:px-6 lg:px-8">
        <nav
          aria-label={t('conferenceBar.settingsSections', 'Conference settings sections')}
          className="flex items-center gap-1 overflow-x-auto scrollbar-none"
        >
          {SETTINGS_SECTIONS.map((s) => {
            const active =
              location?.pathname === s.path && (s.tab || undefined) === currentTab;
            return (
              <Link
                key={s.key}
                to={href(s)}
                aria-current={active ? 'page' : undefined}
                className={`flex items-center gap-1.5 px-2.5 h-10 -mb-px border-b-2 text-[13px] font-medium whitespace-nowrap transition-colors focus-ring ${
                  active
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Icon name={s.icon} size={14} />
                {t(s.label, s.fallback)}
                {s.isNew && (
                  <span className="px-1.5 py-0.5 rounded-full bg-accent text-accent-foreground text-[9px] font-bold uppercase leading-none">
                    {t('conferenceBar.new', 'New')}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
